import React from "react";
import {Grid} from "@mui/material";
import {GlobalKeyVault, Office, Role, UserRole} from "../../api/Model";
import {SetState} from "../../util/FormHandlers";
import {filterSerializer} from "../../util/FilterSerializer";
import {StringEnumFilter} from "../../components/filter-fields/StringEnumFilter";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {Access} from "../../util/access/Access";
import {associateBy} from "../../util/MapUtil";
import {officeIdOfRole, vaultIdOfRole} from "../../util/access/AccessRules";
import {OfficeFilter} from "../../components/filter-fields/OfficeFilter";
import {andFilter, applyFilter, filterTextWithInit, filterValue, matchIfSelected} from "../../util/FilterUtil";

type UserRoleTypeFilter = '' | Role['type'];

export interface UserRolesFilterForm {
    office: Office | null;
    role: UserRoleTypeFilter;
    search: string;
}

const emptyUserRolesFilter: UserRolesFilterForm = {
    office: null,
    role: '',
    search: '',
};

export const userRolesFilterSerializer = (offices: Office[]) => filterSerializer<UserRolesFilterForm>(
    emptyUserRolesFilter,
    b => {
        b.office('office', offices);
        b.str('role');
        b.str('search');
    }
);

const roleFilterOptions: { value: UserRoleTypeFilter, label: string }[] = [
    {value: '', label: 'All Roles'},
    {value: 'guest', label: 'Guest'},
    {value: 'vault-manager', label: 'Vault Manager'},
    {value: 'office-admin', label: 'Office Admin'},
    {value: 'system-admin', label: 'System Admin'},
];

export function roleDisplayName(role: Role): string {
    switch (role.type) {
        case 'guest':
            return 'Guest';
        case 'vault-manager':
            return 'Vault Manager';
        case 'office-admin':
            return 'Office Admin';
        case 'system-admin':
            return 'System Admin';
    }
}

export function filterUserRoles(
    userRoles: UserRole[],
    filter: UserRolesFilterForm,
    offices: Office[],
    keyVaults: GlobalKeyVault[],
): UserRole[] {
    const filterOffice = filter.office;
    return applyFilter(userRoles, andFilter<UserRole>(
        filterValue(
            Access.matchRoleOffice(filterOffice?.id),
            userRole => userRole.role,
        ),
        filterValue(
            matchIfSelected(filter.role, ''),
            userRole => userRole.role.type,
        ),
        filterTextWithInit<UserRole>(filter.search, () => {
            const id2office = associateBy(offices, it => it.id);
            const id2vault = associateBy(keyVaults, it => it.id);
            return userRole => {
                const officeId = officeIdOfRole(userRole.role);
                const vaultId = vaultIdOfRole(userRole.role);
                return [
                    userRole.email,
                    roleDisplayName(userRole.role),
                    userRole.role.type,
                    officeId ? id2office.get(officeId)?.name : undefined,
                    vaultId ? id2vault.get(vaultId)?.name : undefined,
                ];
            };
        }),
    ));
}

export function UserRolesFilter({filter, setFilter}: {
    filter: UserRolesFilterForm,
    setFilter: SetState<UserRolesFilterForm>,
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <OfficeFilter filter={filter} setFilter={setFilter}/>
            <StringEnumFilter label="Role" filter={filter} setFilter={setFilter} property="role"
                              options={roleFilterOptions}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyUserRolesFilter} setFilter={setFilter}/>
        </Grid>
    );
}
